import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
export function DoNotSellMyInfoPage() {
  usePageMeta('Do Not Sell My Info | FindOnlineColleges', 'Exercise your right to opt out of the sale or sharing of your personal information.');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    state: '',
    note: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Opt-out request submitted:', formData);
    // Send the request to the admin inbox via the visitor's email client.
    const subject = 'California Privacy Opt-Out Request';
    const body = ['A new Do Not Sell or Share My Personal Information request has been submitted:', '', `Full Name: ${formData.fullName}`, `Email: ${formData.email}`, `Phone: ${formData.phone || 'N/A'}`, `State of Residence: ${formData.state}`, `Additional Notes: ${formData.note || 'N/A'}`].join('\n');
    const mailtoUrl = `mailto:admin@media-matchers.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
    setIsSubmitted(true);
  };
  return <main className="bg-white py-10 sm:py-16" data-id="element-1464" data-mp="2yvd18rfh80ev">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-1465" data-mp="2yw5xgygt77ir">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-1466" data-mp="2tw0d83cvcnap">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-1467" data-mp="2pwj675hsni3f" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-1468" data-mp="o31pzdfll8x2">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-1469" data-mp="o18tgqyp852s">
            Do Not Sell or Share My Personal Information
          </h1>
        </header>

        {/* Intro */}
        <div className="space-y-5 leading-relaxed text-base mb-12" data-id="element-1470" data-mp="1270qr2kwf13p">
          <p data-id="element-1471" data-mp="1714yvp5whf6d">
            Under the California Consumer Privacy Act (CCPA) and the California
            Privacy Rights Act (CPRA), California residents have the right to
            opt out of the "sale" or "sharing" of their personal information.
          </p>
          <p data-id="element-1472" data-mp="1lwb80l0alu3x">
            <strong data-id="element-1473" data-mp="1lxvzu2v82yy3">What does "sale" or "sharing" mean?</strong> In the context
            of our services, we do not sell your personal information for money.
            However, we may share your information with educational partners to
            fulfill your requests for information about degree programs, or with
            advertising partners to deliver targeted advertisements. Under
            California law, these activities may be considered a "sale" or
            "sharing" of personal information.
          </p>
          <p data-id="element-1474" data-mp="5jw4z6b8hzhp">
            If you would like to exercise your right to opt out of these
            activities, please complete the form below. Alternatively, you may
            email us at{' '}
            <a href="mailto:contact@findonlinecolleges.org" className="text-navy underline hover:text-crimson" data-id="element-1475" data-mp="3p53eg2nupnjl">
              contact@findonlinecolleges.org
            </a>{' '}
            with the subject line "California Privacy Opt-Out".
          </p>
          <p data-id="element-1476" data-mp="3ah098vhmufvu">
            For more details on how we handle your data, please review our{' '}
            <Link to="/privacy-policy" className="text-navy underline hover:text-crimson" data-id="element-1477" data-mp="36ggc3rx27c9p">
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        {/* Form Section */}
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 sm:p-10" data-id="element-1478" data-mp="2rppj4gjqe750">
          {isSubmitted ? <div className="text-center py-8" data-id="element-1479" data-mp="2mrnznuie85t0">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6" data-id="element-1480" data-mp="2motfm43akn04">
                <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-1481" data-mp="30bs5wmhfbnj6" />
              </div>
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1482" data-mp="35alkyjcczmlg">
                Request Received
              </h2>
              <p className="text-gray-600 max-w-md mx-auto" data-id="element-1483" data-mp="3f2ivdnvjmaj4">
                Thank you. We have received your request to opt out of the sale
                or sharing of your personal information. We will process your
                request within 15 business days.
              </p>
            </div> : <>
              <h2 className="text-2xl font-bold text-navy mb-6" data-id="element-1484" data-mp="3t3btsjoytpri">
                Opt-Out Request Form
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1485" data-mp="1vh6mgbmbjqh">
                <div data-id="element-1486" data-mp="1yp14690jqrko">
                  <label htmlFor="fullName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1487" data-mp="1ypklpomabdy6">
                    Full Name <span className="text-crimson" data-id="element-1488" data-mp="ksrbvzy9dglv">*</span>
                  </label>
                  <input type="text" id="fullName" required value={formData.fullName} onChange={e => setFormData({
                ...formData,
                fullName: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1489" data-mp="g1rj96w69kp6" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1490" data-mp="uokqdovjfk6a">
                  <div data-id="element-1491" data-mp="pqj5qzigrcsp">
                    <label htmlFor="email" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1492" data-mp="pr2r6q7bojbh">
                      Email Address <span className="text-crimson" data-id="element-1493" data-mp="3agrzekdit6v7">*</span>
                    </label>
                    <input type="email" id="email" required value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1494" data-mp="3bzs3npvkxhnr" />
                  </div>
                  <div data-id="element-1495" data-mp="2k82oc0hlf9o5">
                    <label htmlFor="phone" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1496" data-mp="2k8u9ehdkdp06">
                      Phone Number{' '}
                      <span className="text-gray-500 font-normal" data-id="element-1497" data-mp="2a9vdlzdwdnul">
                        (Optional)
                      </span>
                    </label>
                    <input type="tel" id="phone" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1498" data-mp="1vm4psrdmcroj" />
                  </div>
                </div>

                <div data-id="element-1499" data-mp="29kl7fdd0s2lg">
                  <label htmlFor="state" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1500" data-mp="2ob28gvlqsqht">
                    State of Residence <span className="text-crimson" data-id="element-1501" data-mp="2jelbdbwkf1sl">*</span>
                  </label>
                  <select id="state" required value={formData.state} onChange={e => setFormData({
                ...formData,
                state: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1502" data-mp="2x9xeo7ose0l4">
                    <option value="" data-id="element-1503" data-mp="hhf3mn9z2wii">Select a state</option>
                    <option value="CA" data-id="element-1504" data-mp="3h068i6yllrnw">California</option>
                    <option value="Other" data-id="element-1505" data-mp="3lxpyg44nrsvo">Other</option>
                  </select>
                </div>

                <div data-id="element-1506" data-mp="3pztbgx6z75j2">
                  <label htmlFor="note" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1507" data-mp="3pyi9n2x3c7ul">
                    Additional Notes{' '}
                    <span className="text-gray-500 font-normal" data-id="element-1508" data-mp="2y7c58aac7nzi">
                      (Optional)
                    </span>
                  </label>
                  <textarea id="note" rows={4} value={formData.note} onChange={e => setFormData({
                ...formData,
                note: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy resize-none" placeholder="Please provide any additional details regarding your request..." data-id="element-1509" data-mp="327m8vganq2b7" />
                </div>

                <button type="submit" className="w-full bg-cta hover:bg-cta-dark text-white font-bold py-4 px-6 rounded-lg text-base transition-colors duration-200 shadow-sm hover:shadow-md" data-id="element-1510" data-mp="221ahc5hs85i2">
                  Submit Opt-Out Request
                </button>
              </form>
            </>}
        </div>
      </div>
    </main>;
}